import { Anchor, AnchorButton, Badge, ButtonAsLink, Link } from '@cluey/cluey-components';
import classNames from 'classnames';
import { isUndefined, noop } from 'lodash';
import moment from 'moment';
import { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router';

import { api } from '../../../../api';
import alertIcon from '../../../../assets/images/icon-filled-alert-exclamation.svg';
import menuBallsIcon from '../../../../assets/images/icon-outline-menu-meatball.svg';
import { InlineLoader } from '../../../../common/InlineLoader';
import SessionTag from '../../../../common/SessionTag/SessionTag';
import env from '../../../../env';
import { getSpecificActiveEnrolment } from '../../../../selectors/enrolmentSelector';
import { getPkgTutorNameByEnrolmentId } from '../../../../selectors/hubEnrolmentsSelector';
import { PAYMENT_STATUS } from '../../../../types/hubEnums';
import type { Session, SessionPolicy } from '../../../../types/hubInterfaces';
import {
  DATE_FORMAT_TYPE_13,
  STUDENT_PROGRESS_TAB_VIEWS,
  STUDENT_PROGRESS_URL_PARAM_TAB,
  TTBM_CUSTOMER_NUMBER,
} from '../../../../util/constants';
import isSessionJoinable from '../../../../util/helper/isSessionJoinable';
import { canSessionBeRescheduled, sessionStartsInLessThanADay } from '../../../../util/helper/sessions';
import { formatSession } from '../../../../util/helpers';
import {
  PATH_HUB_RESCHEDULE_SESSION,
  PATH_HUB_RESCHEDULE_SESSION_WITH_TUTOR,
  PATH_HUB_SESSION_MESSAGE,
  PATH_HUB_SKIP_SESSION,
  PATH_NAME_REPORT,
} from '../../../../util/pagePath';
import { usePostHogCapture } from '../../../../util/posthog';
import { DesktopOnly } from '../../../DesktopOnly/DesktopOnly';
import ChangeSessionModal from '../../../Modals/ChangeSessionModal/ChangeSessionModal';
import ManageUpcomingSessionModal from '../../../Modals/ManageUpcomingSessionModal/ManageUpcomingSessionModal';
import { Tooltip } from '../../../Tooltip/Tooltip';
import SessionPaymentInfo from '../SessionPaymentInfo';
import { ListItemHeader } from './ListItemHeader';
import { SessionDetails } from './SessionDetails';
import { useLiveUpdateSessionStatus } from './hooks';

interface Props {
  session: Session;
  timezone: string;
  isPreview?: boolean;
  policy?: SessionPolicy;
}

const HubSessionsListItem = ({ session, timezone, isPreview = false, policy }: Props) => {
  const {
    student,
    subject,
    tutor,
    tutorNumber,
    uniqueEnrolmentId,
    paymentStatus,
    studentSessionId,
    tutorRescheduleRequestStatusC,
    sessionReportId,
    type,
    isFull,
    conflictingSession,
  } = session;
  const isMaintenanceMode = env.REACT_APP_MAINTENANCE_MODE;
  const isTodaySession = moment(session.startTime).isSame(new Date(), 'day');
  const showJoinButton = isSessionJoinable(session);
  const isClassroomSession = type === 'Classroom';
  const [changeSessionModalOpen, setChangeSessionModalOpen] = useState(false);
  const [classroomIsFull, setClassroomIsFull] = useState(isFull);
  const enrolment = useSelector(getSpecificActiveEnrolment({ enrolmentId: uniqueEnrolmentId }));

  const capture = usePostHogCapture();
  const history = useHistory();

  const sessionProgress = useLiveUpdateSessionStatus({
    startTime: session.startTime,
  });
  const showUpcomingSessionManageOptions = !showJoinButton && sessionProgress !== 'In progress';
  const canBeRescheduled = canSessionBeRescheduled(session, policy);
  const canBeCancelled = ![PAYMENT_STATUS.PENDING_PAYMENT, PAYMENT_STATUS.FAILED].includes(
    session.paymentStatus as PAYMENT_STATUS
  );

  const sessionIdParam = studentSessionId ? { sessionId: studentSessionId } : undefined;
  const pathWithSessionId = (path: string) => (sessionIdParam ? generatePath(path, sessionIdParam) : '');

  const { mutate: joinClassroom, isLoading: generatingClassroomLink } = api.classroom.joinClassroom.useMutation({
    onSuccess(data) {
      const { classroomLink, studentToken } = data;
      window.open(`${classroomLink}/latest/?token=${studentToken}`, '_blank');
    },
    onError() {
      setClassroomIsFull(true);
    },
  });

  const closeChangeSessionModal = () => {
    setChangeSessionModalOpen(false);
  };
  const openChangeSessionModal = () => {
    capture('click_session-manage');
    setChangeSessionModalOpen(true);
  };
  const getBoxBackgroundColor = () => {
    if (paymentStatus === 'Failed') {
      return 'bg-red-1';
    }
    if (tutorRescheduleRequestStatusC === 'Requested') {
      return 'bg-yellow-1';
    }
    if (showJoinButton) {
      return 'bg-blue-1';
    }
    return 'bg-white';
  };

  let badge: ReactNode | null = null;
  if (session.tutorRescheduleRequestStatusC === 'Approved') {
    badge = (
      <Badge className="flex min-h-[20px] flex-shrink-0 items-center rounded-xl !bg-slate-3 py-px !font-bold">
        Rescheduled with Tutor
      </Badge>
    );
  }

  if (session.tutorRescheduleRequestStatusC === 'Requested') {
    badge = (
      <Badge className="flex h-[20px] flex-shrink-0 items-center rounded-xl py-px !font-bold">
        Tutor Can't Make It
      </Badge>
    );
  }

  if (session.enrolmentType === 'Trial') {
    badge = (
      <Badge className="flex h-[20px] flex-shrink-0 items-center rounded-xl !bg-slate-3 py-px !font-bold">Trial</Badge>
    );
  }

  const packageTutor = useSelector((state) => getPkgTutorNameByEnrolmentId(state, uniqueEnrolmentId));
  const displayTutor = tutorNumber === TTBM_CUSTOMER_NUMBER ? packageTutor : tutor;
  const { date, time } = formatSession({
    session,
    timezone,
    sessionDateFormat: DATE_FORMAT_TYPE_13,
    useSemiColon: false,
  });
  const isRescheduleRequested = session.tutorRescheduleRequestStatusC === 'Requested';

  const renderManageOptions = (className?: string) => (
    <>
      {!isMaintenanceMode && !isClassroomSession && (
        <div className={classNames('flex flex-col items-start gap-1 lg:items-end', className)}>
          {isTodaySession && (
            <ButtonAsLink
              onClick={isPreview ? noop : openChangeSessionModal}
              className="underline underline-offset-4"
              size="base"
            >
              Can't make it
            </ButtonAsLink>
          )}
          {showUpcomingSessionManageOptions && (
            <>
              {canBeRescheduled && (
                <ButtonAsLink
                  onClick={() => history.push(pathWithSessionId(PATH_HUB_RESCHEDULE_SESSION))}
                  className="underline underline-offset-4"
                  size="base"
                >
                  Reschedule session
                </ButtonAsLink>
              )}
              {canBeCancelled && !isTodaySession && (
                <ButtonAsLink
                  onClick={() => history.push(pathWithSessionId(PATH_HUB_SKIP_SESSION))}
                  className="underline underline-offset-4"
                  size="base"
                >
                  Cancel session
                </ButtonAsLink>
              )}
              <ButtonAsLink
                onClick={() => history.push(generatePath(PATH_HUB_SESSION_MESSAGE, { sessionId: studentSessionId }))}
                className="underline underline-offset-4"
                size="base"
              >
                Message tutor
              </ButtonAsLink>
            </>
          )}
          <ButtonAsLink
            onClick={isPreview ? noop : openChangeSessionModal}
            className="hidden flex-row items-center py-1 lg:flex"
            size="base"
          >
            <img src={menuBallsIcon} className="h-5 w-5" alt="menu balls icon" />
          </ButtonAsLink>
        </div>
      )}
      {isClassroomSession && (
        <div className={classNames('flex flex-col items-start gap-1 lg:items-end', className)}>
          <span>
            <Anchor
              href={`${PATH_NAME_REPORT}?token=${sessionReportId}&${STUDENT_PROGRESS_URL_PARAM_TAB}=${STUDENT_PROGRESS_TAB_VIEWS.SESSION_REPORT}`}
              target="_blank"
              size="base"
              className="underline underline-offset-4"
            >
              View student progress
            </Anchor>
          </span>
          <span>
            <Anchor
              href={`${PATH_NAME_REPORT}?token=${sessionReportId}&${STUDENT_PROGRESS_URL_PARAM_TAB}=${STUDENT_PROGRESS_TAB_VIEWS.ASSESSMENTS}`}
              target="_blank"
              size="base"
              className="underline underline-offset-4"
            >
              View practice questions
            </Anchor>
          </span>
          <span>
            <Anchor
              href={`${PATH_NAME_REPORT}?token=${sessionReportId}&${STUDENT_PROGRESS_URL_PARAM_TAB}=${STUDENT_PROGRESS_TAB_VIEWS.PROGRESS_OVERVIEW}`}
              target="_blank"
              size="base"
              className="underline underline-offset-4"
            >
              View upcoming topics
            </Anchor>
          </span>
          {/* TODO: Post MVP: Implement manage subscription */}
          {/* <span>Manage subscription</span> */}
        </div>
      )}
    </>
  );

  return (
    <div
      data-testid="session-list-item"
      className={classNames(
        'relative flex w-full flex-col items-start gap-1 rounded-lg border-2 border-grey-2 px-5 py-3 shadow-md xl:flex',
        getBoxBackgroundColor()
      )}
    >
      {sessionStartsInLessThanADay({ session, timezone }) ? (
        // Can't make it modal options
        <ManageUpcomingSessionModal
          open={changeSessionModalOpen}
          closeModal={closeChangeSessionModal}
          session={session}
        />
      ) : (
        // Manage ongoing session options
        <ChangeSessionModal
          open={changeSessionModalOpen}
          closeModal={closeChangeSessionModal}
          session={session}
          enrolment={enrolment}
        />
      )}
      <div className="flex w-full flex-col lg:flex-row">
        <div className="flex w-full grow basis-2/3 flex-col items-start gap-1.5">
          <div className="flex w-full flex-row items-center justify-between gap-2 lg:hidden">
            <span>
              <ListItemHeader session={session} sessionStatus={sessionProgress} />
            </span>
            <ButtonAsLink
              onClick={isPreview ? noop : openChangeSessionModal}
              className="flex flex-row items-center py-1"
              size="base"
            >
              <img src={menuBallsIcon} className="h-5 w-5" alt="menu balls icon" />
            </ButtonAsLink>
          </div>
          <div className="flex flex-col">
            <div className="flex flex-row flex-wrap items-center gap-2 lg:items-center lg:gap-3">
              <SessionDetails student={student} subject={subject} tutor={displayTutor} />
              {badge}
              <span className="hidden lg:block">
                <ListItemHeader session={session} sessionStatus={sessionProgress} />
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            {isRescheduleRequested && (
              <Link
                to={generatePath(PATH_HUB_RESCHEDULE_SESSION_WITH_TUTOR, {
                  sessionId: studentSessionId,
                })}
                size="base"
              >
                Reschedule with {tutor}
              </Link>
            )}
            <span className="text-xs font-bold uppercase">{isTodaySession ? 'Today' : date}</span>
            <span className="text-base">{time}</span>
            {isClassroomSession && !isUndefined(conflictingSession) && (
              <div className="flex flex-row items-start gap-x-2 pt-2 lg:items-center lg:pt-0">
                <img src={alertIcon} className="h-5 w-5" alt="alert icon" />
                <span className="font-bold">
                  This class clashes with your{' '}
                  {conflictingSession.type === 'Course' ? '1-on-1' : conflictingSession.type}{' '}
                  {conflictingSession.subject} session (with {conflictingSession.tutor})
                </span>
              </div>
            )}
          </div>
          {!isMaintenanceMode && !isClassroomSession && <SessionPaymentInfo session={session} />}
          <SessionTag session={session} policy={policy} className="my-1.5" />
          <DesktopOnly className={classNames(showJoinButton ? 'flex' : 'hidden')} />
          <p className="block text-base text-grey-6 lg:hidden">{sessionProgress}</p>
          {renderManageOptions('flex lg:hidden')}
        </div>
        <div className="hidden w-full basis-1/3 flex-col items-end gap-1 lg:flex">
          <div>
            {showJoinButton && paymentStatus !== 'Failed' && !classroomIsFull ? (
              <AnchorButton
                size="sm"
                href={!isPreview && !isClassroomSession ? session.sessionToken : undefined}
                onClick={() => {
                  capture('click_session-join');
                  if (isClassroomSession) {
                    joinClassroom({
                      sessionId: session.sessionToken,
                      enrolment,
                    });
                  }
                }}
                className="mb-1 hidden min-w-[80px] !px-6 normal-case lg:inline-flex lg:items-center lg:justify-center"
              >
                {generatingClassroomLink && <InlineLoader />}
                <span className={classNames(generatingClassroomLink && '!text-transparent')}>Join now</span>
              </AnchorButton>
            ) : (
              <div className="flex flex-col items-start gap-1 lg:items-end">
                {classroomIsFull ? (
                  <p className="proxima-soft font-bold" style={{ color: '#DA392B' }}>
                    Class is full
                  </p>
                ) : (
                  <>
                    {paymentStatus !== 'Failed' && (
                      <Tooltip content="You can join this session 15 minutes before it starts.">
                        <AnchorButton
                          size="sm"
                          href={undefined}
                          className="mb-1 hidden min-w-[80px] cursor-not-allowed !border-grey-2 !bg-grey-2 !px-6 normal-case !text-grey-5 lg:inline-flex lg:items-center lg:justify-center"
                        >
                          <span className={classNames(generatingClassroomLink && '!text-transparent')}>Join now</span>
                        </AnchorButton>
                      </Tooltip>
                    )}
                    <p className={classNames('text-sm text-grey-6 lg:block', paymentStatus !== 'Failed' && '!text-sm')}>
                      {sessionProgress}
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
          <div>{renderManageOptions()}</div>
        </div>
      </div>
    </div>
  );
};

export default HubSessionsListItem;
